import React, {useEffect, useState} from 'react';

import {
  FILTERS,
  Operator,
  STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY,
  StandardFilter,
  StandardFilterDataType,
  VALUELESS_OPERATORS,
} from 'search/SearchConstants';
import {FILTER_ENUM_CONFIGS} from 'search/SearchEnumFilterConfigs';
import {OptionData} from 'ui-library/menus/MenuConstants';
import pickDefined from 'util/pickDefined';

import DropdownFilterForm from './DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import OperatorDropdown from './OperatorDropdown';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';

export interface StandardEnumFilterFormProps extends BaseFilterFormProps {
  filter: StandardFilter;
  filterStyle: FilterStyle;
  operators: Array<Operator>;
  onChangeFilter: (filter: StandardFilter) => void;
  onRemove?: () => void;
  onEnter?: () => void;
}

export default function StandardEnumFilterForm(props: StandardEnumFilterFormProps): JSX.Element {
  const [optionDatas, setOptionDatas] = useState(null as Array<OptionData> | null);

  const filterKey = props.filter.key;
  if (!FILTERS[filterKey].dataType) {
    throw new Error(`Standard filter ${filterKey} is missing a data type.`);
  }
  const filterDataType: StandardFilterDataType = FILTERS[filterKey].dataType;

  if (filterDataType !== StandardFilterDataType.ENUM) {
    throw new Error(`Standard filter ${filterKey} is not an enum filter. Use StandardFilterForm instead.`);
  }

  if (!FILTER_ENUM_CONFIGS[filterKey]) {
    throw new Error(`Standard enum filter ${filterKey} is missing an enum config.`);
  }

  useEffect(() => {
    let ignore = false;
    const enumConfig = FILTER_ENUM_CONFIGS[filterKey]!;

    enumConfig.getOptionDatas().then((optionDatas) => {
      if (!ignore) {
        setOptionDatas(optionDatas);
      }
    });

    return () => {
      ignore = true;
    };
  }, [filterKey]);

  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);

  const onChangeOperator = (operator: Operator): void => {
    const defaultValueFactory = STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY[operator];
    if (!defaultValueFactory) {
      throw new Error(
        `Attempted to select operator ${operator} for standard filter, but no default value factory was found.`
      );
    }
    props.onChangeFilter({...props.filter, operator, value: defaultValueFactory()});
  };

  if (VALUELESS_OPERATORS.includes(props.filter.operator)) {
    const operatorDropdown: JSX.Element = (
      <OperatorDropdown
        buttonSize={props.buttonSize}
        onChangeOperator={onChangeOperator}
        operators={props.operators}
        selectedOperator={props.filter.operator}
      />
    );
    const nodeGrid: Array<Array<JSX.Element>> = [[operatorDropdown]];
    if (props.filterStyle === FilterStyle.PRIMARY) {
      return (
        <PrimaryFilterFormSkeleton
          {...skeletonProps}
          content={<FilterFormSkeletonNodeLayout nodeGrid={nodeGrid} />}
          dropdownToggleText={props.dropdownToggleText}
        />
      );
    } else {
      if (!props.onRemove) {
        throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
      }
      return (
        <SecondaryFilterFormSkeleton
          {...skeletonProps}
          nodeGrid={nodeGrid}
          onChangeFilterKey={props.onChangeFilterKey}
          selectedFilterKey={props.selectedFilterKey}
          onRemove={props.onRemove}
        />
      );
    }
  } else {
    return (
      <DropdownFilterForm
        {...skeletonProps}
        operators={props.operators}
        isLoading={optionDatas == null}
        filterStyle={props.filterStyle}
        isMulti={true}
        isSearchable={FILTER_ENUM_CONFIGS[filterKey].isSearchable}
        onChangeOperator={onChangeOperator}
        onChangeValue={(value: Array<string>) => {
          props.onChangeFilter({...props.filter, value});
        }}
        onRemove={props.onRemove}
        optionDatas={optionDatas || []}
        selectedOperator={props.filter.operator}
        value={props.filter.value as Array<string>}
      />
    );
  }
}
